<script setup lang="ts">
import type { Video } from "~/types/media"
import { getPendo } from "~/utilities/pendo"
import { toDisplayLanguage } from "../../utilities/videoUtils"

const {
  video,
  title,
  isDark = true,
  autoPlay = false,
} = defineProps<{
  video: Video
  title?: string
  isDark?: boolean
  autoPlay?: boolean
}>()

const videoElement = ref<HTMLVideoElement | null>(null)
const isPlaying = ref(false)
const videoLoaded = ref(false)
const showTime = ref(true)
const showTranscription = ref(false)

const PROGRESS_MILESTONES = [25, 50, 75, 95]
const reachedMilestones = ref(new Set<number>())

const trackingInfo = computed(() => ({
  title: title ?? video.title ?? "-",
  videoId: video.id,
}))

function onLoad() {
  videoLoaded.value = true
}

const time = computed(() => {
  if (!video.pathString) {
    return undefined
  }

  return getMinutesAndSeconds(video.metadata?.duration, "")
})

function toggleVideoPlay() {
  if (
    videoElement.value &&
    (!isPlaying.value || videoElement.value.paused)
  ) {
    if (showTime.value) {
      showTime.value = false
    }
    videoElement.value.play()
    isPlaying.value = true
  } else {
    videoElement.value?.pause()
    isPlaying.value = false
  }
}

function convertToSeconds(time: string) {
  return time
    .split(":")
    .reverse()
    .reduce(
      (prev, curr, i) =>
        prev + (curr as unknown as number) * (60 ** i),
      0,
    )
}

function handlePlayClick(time: string) {
  const seconds = convertToSeconds(time)

  if (!videoElement.value)
    return

  videoElement.value.currentTime = seconds
  isPlaying.value = true
  showTime.value = false
  videoElement.value.play()
}

function closeTranscription() {
  showTranscription.value = false
}

function checkProgress() {
  if (!videoElement.value || videoElement.value.duration === 0)
    return

  const progress = (
    (videoElement.value.currentTime / videoElement.value.duration) *
    100
  ).toFixed(0)

  const highestMilestone = PROGRESS_MILESTONES.sort(
    (a, b) => b - a,
  ).find(milestone => Number(progress) >= milestone)

  if (
    highestMilestone &&
    !reachedMilestones.value.has(highestMilestone)
  ) {
    reachedMilestones.value.add(highestMilestone)

    getPendo()?.track("Video progress", {
      progress: highestMilestone,
      ...trackingInfo.value,
    })
  }
}

function onPlay() {
  isPlaying.value = true

  if (videoElement.value?.currentTime === 0) {
    getPendo()?.track("Video start", {
      ...trackingInfo.value,
    })
  }
}

function onPause() {
  isPlaying.value = false

  if (videoElement.value?.currentTime) {
    getPendo()?.track("Video pause", {
      ...trackingInfo.value,
      currentTime: videoElement.value.currentTime,
    })
  }
}

const playbackRate = ref(1)

function onRateChange() {
  const newRate = videoElement.value?.playbackRate
  if (newRate) {
    playbackRate.value = newRate
  }
}

watch(playbackRate, (newValue, previousValue) => {
  getPendo()?.track("Speed change", {
    speed: newValue,
    previousSpeed: previousValue,
    ...trackingInfo.value,
  })
})
</script>

<template>
  <section class="@container">
    <div
      class="my-10 flex w-full flex-col gap-4 self-center @lg:flex-row"
    >
      <div
        class="aspect-w-16 aspect-h-9 w-full"
        :class="{
          '@xl:w-2/3': showTranscription,
          '@xl:w-full': !showTranscription,
        }"
      >
        <div class="relative size-full">
          <UButton
            v-if="!isPlaying && videoLoaded"
            circle
            theme="cta"
            variant="filled"
            class="absolute inset-0 z-20 m-auto"
            aria-label="spill av video"
            aria-controls="video-player"
            @click="toggleVideoPlay"
          >
            <template #prepend>
              <UIcon
                name="play-filled"
                size="lg"
                class="p-2"
              />
            </template>
          </UButton>
          <video
            v-if="video.metadata.elementURI"
            id="video-player"
            ref="videoElement"
            preload="auto"
            :captions="video.captions"
            controls
            controlsList="nodownload"
            :poster="video.thumbnail"
            crossorigin="anonymous"
            playsinline
            :src="video.metadata.elementURI"
            tabindex="0"
            class="my-auto size-full rounded-2xl bg-black object-contain"
            :autoplay="autoPlay"
            @loadeddata="onLoad"
            @play="onPlay"
            @timeupdate="checkProgress"
            @pause="onPause"
            @ratechange="onRateChange"
          >
            <track
              v-for="caption in video.captions"
              :key="caption.captionFile.uri"
              :src="caption.captionFile.uri"
              :srclang="caption.language"
              :label="toDisplayLanguage(caption.language)"
            />
            <span
              v-if="showTime && time"
              class="national-2 absolute left-4 top-4 hidden rounded-lg bg-darker p-2 text-2xl font-extrabold text-platform-white md:block"
              v-text="time"
            />
            <span class="sr-only">minutter lengde</span>
          </video>
        </div>
      </div>
      <UVideoTranscription
        v-if="showTranscription"
        id="transcription"
        class="hidden @xl:flex"
        :transcription="video.transcription"
        :close="closeTranscription"
      />
    </div>
    <div
      class="flex flex-col flex-wrap items-start justify-between gap-4 self-start @xl:flex-row-reverse"
    >
      <div class="flex w-full flex-col @xl:w-fit @xl:flex-row">
        <UButton
          :theme="isDark ? 'light' : 'black'"
          size="sm"
          variant="quite"
          data-pendo="video-transcription"
          @click="showTranscription = !showTranscription"
        >
          <template #prepend>
            <UIcon name="align-left" />
          </template>
          {{
            showTranscription
              ? "Skjul transkripsjon"
              : "Vis transkripsjon"
          }}
        </UButton>
        <UVideoTranscription
          v-if="showTranscription"
          id="transcription"
          class="@xl:hidden"
          :transcription="video.transcription"
          :close="closeTranscription"
          :is-dark="isDark"
        />
      </div>
      <div
        v-if="video.timestamps.length"
        class="flex w-full flex-wrap gap-4 @sm:max-w-[70%] @md:w-[60vw] @lg:max-w-2/4"
      >
        <UButton
          v-for="timestamp in video.timestamps"
          :key="timestamp.id"
          theme="translucent-light"
          pill
          size="sm"
          variant="filled"
          @click="handlePlayClick(timestamp.time)"
        >
          {{ timestamp.time }} {{ timestamp?.description }}
        </UButton>
      </div>
    </div>
  </section>
</template>
